import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { onPlanLimit } from '../utils/planLimit';

/**
 * What this account is allowed to do, and the one place the upgrade dialog is opened from.
 *
 * The server is the authority: everything here is display, never enforcement. `limits.liveFlows`
 * of `null` means unlimited (the wire uses null too, so there is no sentinel to keep in step),
 * and the safe default before the first response has landed is the Free shape, an account that
 * briefly looks more restricted than it is shows one extra hint; the reverse hides a limit the
 * server is about to apply.
 */
const EntitlementsContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL;

export const FREE_ENTITLEMENTS = {
  plan: 'FREE',
  limits: { liveFlows: 1, numbers: 1, environments: 1, workspaces: 1 },
  brandingRequired: true,
  features: { deliveries: false, intentTriggers: false, booking: false },
  billing: { configured: false, status: null, currentPeriodEnd: null, cancelAtPeriodEnd: false, manageable: false },
};

export const EntitlementsProvider = ({ children }) => {
  const { isAuthenticated, authenticatedFetch } = useAuth();
  const [entitlements, setEntitlements] = useState(FREE_ENTITLEMENTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [planLimit, setPlanLimit] = useState(null);

  // authenticatedFetch is rebuilt on every AuthContext render; pinning it keeps `refresh` stable
  // so an effect depending on it does not re-run forever.
  const fetchRef = useRef(authenticatedFetch);
  fetchRef.current = authenticatedFetch;

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setEntitlements(FREE_ENTITLEMENTS);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRef.current(`${API_URL}/api/me/entitlements`);
      if (!response.ok) throw new Error(`Failed to load your plan (${response.status})`);
      setEntitlements(await response.json());
    } catch (err) {
      // A failure here must not break the app: keep the Free shape and say so.
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Any 402 PLAN_LIMIT from any request, from any page.
  useEffect(() => onPlanLimit(setPlanLimit), []);

  const value = useMemo(() => ({
    entitlements,
    plan: entitlements?.plan ?? 'FREE',
    limits: entitlements?.limits ?? FREE_ENTITLEMENTS.limits,
    features: entitlements?.features ?? FREE_ENTITLEMENTS.features,
    billing: entitlements?.billing ?? FREE_ENTITLEMENTS.billing,
    brandingRequired: entitlements?.brandingRequired ?? true,
    isFree: (entitlements?.plan ?? 'FREE') === 'FREE',
    loading,
    error,
    refresh,
    planLimit,
    showPlanLimit: setPlanLimit,
    dismissPlanLimit: () => setPlanLimit(null),
  }), [entitlements, loading, error, refresh, planLimit]);

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
};

/**
 * Usable outside the provider (it falls back to the Free shape), so a component can read the
 * plan without every test that renders it having to wrap it.
 */
export const useEntitlements = () => {
  const context = useContext(EntitlementsContext);
  if (context) return context;
  return {
    entitlements: FREE_ENTITLEMENTS,
    plan: 'FREE',
    limits: FREE_ENTITLEMENTS.limits,
    features: FREE_ENTITLEMENTS.features,
    billing: FREE_ENTITLEMENTS.billing,
    brandingRequired: true,
    isFree: true,
    loading: false,
    error: null,
    refresh: () => {},
    planLimit: null,
    showPlanLimit: () => {},
    dismissPlanLimit: () => {},
  };
};

export default EntitlementsContext;
